import { useId, useState } from 'react'
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  AppLayout,
  AppWindowIcon,
  CalendarBlankIcon,
  CalendarXIcon,
  ClockIcon,
  DetailsCard,
  EyeIcon,
  HashIcon,
  MarkdownContent,
  Mtds,
  NewsImage,
  NewspaperIcon,
  PencilSimpleIcon,
  ScreenHeader,
  ScreenLayout,
  TagIcon,
  TopBar,
  TrashIcon,
  WhatsNew,
  useAuth,
  type ScreenHeaderProperty
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { CopyReferenceValue } from '../tables/reference-table/ReferenceTable'
import { NewsDeleteDialog } from '../components/news-delete-dialog/NewsDeleteDialog'
import { newsApiError, newsUserMessage } from '../utils/newsApi'
import { formatNewsDate } from '../utils/newsDates'
import {
  newsAppLabel,
  newsBody,
  newsSummary,
  newsTypeLabel,
  useNewsExpired
} from '../utils/newsArticle'
import '../components/news/news.css'

export const NewsArticleScreen = () => {
  const { isAdmin } = useAuth()
  return isAdmin ? <NewsArticle /> : <Navigate to='/unauthorized' replace />
}

function NewsArticle() {
  const { newsId } = useParams<{ newsId: string }>()
  const { search } = useLocation()
  const navigate = useNavigate()
  const { authFetch } = useAuth()
  const contextMenuId = useId()
  const [previewOpen, setPreviewOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const articleId = Number(newsId)
  const validId =
    /^\d+$/.test(newsId ?? '') &&
    Number.isSafeInteger(articleId) &&
    articleId > 0
  const listTo = `/news${search}`
  const query = useQuery({
    queryKey: ['newsArticle', articleId],
    enabled: validId,
    retry: false,
    queryFn: async () => {
      const result = await authFetch().GET('/news/{articleId}', {
        params: { path: { articleId } }
      })
      if (!result.response.ok || result.error) throw newsApiError(result.error)
      if (!result.data) throw new Error('Artikkelen finnes ikke.')
      return result.data
    }
  })
  const article = query.data
  const expired = useNewsExpired(article?.endDate)
  const summary = article ? newsSummary(article) : 'Nyhetsartikkel'

  const headerProperties: ScreenHeaderProperty[] = [
    {
      label: 'ID',
      icon: <HashIcon aria-hidden />,
      value: article ? (
        <CopyReferenceValue value={article.id} label='ID' />
      ) : (
        '-'
      )
    },
    {
      label: 'Status',
      icon: <ClockIcon aria-hidden />,
      value: article ? (
        <Mtds.Tag data-color={expired ? 'neutral' : 'success'}>
          {expired ? 'Utløpt' : 'Tilgjengelig'}
        </Mtds.Tag>
      ) : (
        '-'
      )
    }
  ]

  const detailProperties: ScreenHeaderProperty[] = [
    {
      label: 'App',
      icon: <AppWindowIcon aria-hidden />,
      value: article ? newsAppLabel(article.app) : '-'
    },
    {
      label: 'Type',
      icon: <TagIcon aria-hidden />,
      value: article ? newsTypeLabel(article.icon) : '-'
    },
    {
      label: 'Artikkeldato',
      icon: <CalendarBlankIcon aria-hidden />,
      value: article ? formatNewsDate(article.date) : '-'
    },
    {
      label: 'Siste visningsdag',
      icon: <CalendarXIcon aria-hidden />,
      value: article ? formatNewsDate(article.endDate) : '-'
    }
  ]

  return (
    <AppLayout
      useMobileBar={false}
      isFullWidth
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={[
            { label: 'Nyheter', to: listTo },
            { label: summary, to: `/news/${newsId}${search}` }
          ]}
        />
      }
    >
      <ScreenLayout
        mainMaxWidth={1000}
        details={
          article && (
            <DetailsCard
              title='Detaljer'
              icon={<NewspaperIcon aria-hidden />}
              properties={detailProperties}
            />
          )
        }
        header={
          <ScreenHeader
            title={summary}
            properties={headerProperties}
            actions={
              <>
                {article ? (
                  <Mtds.Button
                    as={Link}
                    to={`/news/${newsId}/edit${search}`}
                    data-variant='primary'
                  >
                    <PencilSimpleIcon />
                    Rediger artikkel
                  </Mtds.Button>
                ) : (
                  <Mtds.Button type='button' data-variant='primary' disabled>
                    <PencilSimpleIcon />
                    Rediger artikkel
                  </Mtds.Button>
                )}
                <Mtds.Button
                  type='button'
                  aria-label='Handlingsmeny'
                  disabled={!article}
                  popoverTarget={contextMenuId}
                />
                <Mtds.Popover as='menu' id={contextMenuId}>
                  <li>
                    <Mtds.Button
                      type='button'
                      popoverTargetAction='hide'
                      onClick={() => setPreviewOpen(true)}
                    >
                      <EyeIcon />
                      Forhåndsvis
                    </Mtds.Button>
                  </li>
                  <li>
                    <Mtds.Button
                      type='button'
                      data-color='danger'
                      popoverTargetAction='hide'
                      onClick={() => setConfirmDelete(true)}
                    >
                      <TrashIcon />
                      Slett artikkel
                    </Mtds.Button>
                  </li>
                </Mtds.Popover>
              </>
            }
          />
        }
      >
        {!validId && (
          <Mtds.Alert data-color='danger'>Ugyldig artikkel-ID.</Mtds.Alert>
        )}
        {validId && query.isFetching && (
          <p role='status'>
            {article
              ? 'Oppdaterer artikkelen. Viser sist hentede data.'
              : 'Laster artikkel …'}
          </p>
        )}
        {validId && query.isError && (
          <Mtds.Alert as='div' role='alert' data-color='danger'>
            <Mtds.Grid>
              <p>{newsUserMessage(query.error)}</p>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                disabled={query.isFetching}
                aria-busy={query.isFetching}
                onClick={() => void query.refetch()}
              >
                Prøv igjen
              </Mtds.Button>
            </Mtds.Grid>
          </Mtds.Alert>
        )}
        {validId && article && (
          <Mtds.Grid data-gap='5'>
            <Mtds.Heading as='h2' data-size='sm'>
              Innhold
            </Mtds.Heading>
            {newsBody(article) ? (
              <MarkdownContent content={newsBody(article)} />
            ) : (
              <Mtds.Muted>Artikkelen har ingen brødtekst.</Mtds.Muted>
            )}
            <NewsImage
              key={article.id}
              articleId={article.id}
              className='news-image-preview'
            />
          </Mtds.Grid>
        )}
      </ScreenLayout>
      {article && (
        <WhatsNew
          article={article}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {confirmDelete && article && (
        <NewsDeleteDialog
          article={article}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => void navigate(listTo)}
        />
      )}
    </AppLayout>
  )
}
